/*
 * section-main-blog.js — Slice 24 Phase B1 (Blog index).
 *
 * Progressive enhancement for sections/main-blog.liquid. Loaded via
 * <script defer> ONLY when the section actually needs it (view switcher
 * shown OR sort shown OR search trigger shown OR pagination is
 * load_more/infinite). Browser dedupes by URL; a per-section dataset guard
 * plus a one-time global guard keep multi-instance / re-injection safe.
 *
 * Four behaviours, all per main-blog instance unless noted:
 *
 *   1. View switch (grid <-> list). Clicking a .segmented-control__btn sets
 *      data-view on EVERY [data-blog-grid] in the section (one flat grid, or
 *      one per year when year-grouping is on) and persists the choice to
 *      localStorage (kv-blog-view:<handle>). On load the stored value (if
 *      valid) wins over the merchant default already on the grid. The active
 *      segment's aria-pressed is set HERE from the applied view, never from
 *      Liquid (Rule S20 — the server doesn't know the visitor's choice).
 *      Skipped in Shopify.designMode (persistence only).
 *
 *   2. Sort (Newest / Oldest). "Newest" is the native server order; "Oldest"
 *      reverses. Each card carries a JS-assigned data-natural-index (monotonic
 *      across load-more appends), and applySort() re-orders by it — idempotent,
 *      no double-flip. Scope is the currently-loaded set (documented limit).
 *      Persisted to kv-blog-sort:<handle>.
 *
 *   3. Load-more / infinite. Over the numbered pager (the no-JS baseline).
 *      Fetches paginate.next.url, parses, appends the next page's .article-card
 *      elements into the flat grid, and reads the fetched page's own
 *      [data-blog-load-more] for the following next-url. Infinite adds an
 *      IntersectionObserver sentinel. Only emitted for the flat grid (NOT when
 *      year-grouping is on — appending across year buckets is incoherent;
 *      year-grouping falls back to the numbered pager).
 *
 *   4. Search trigger aria-expanded (Adjustment #4 — non-optional). The global
 *      predictive-search drawer does NOT toggle aria-expanded on its
 *      [data-open-search-drawer] triggers, and dispatches NO close event — it
 *      only dispatches 'search:open' and closes internally by removing .is-open
 *      from <predictive-search>. So this script reflects the drawer state onto
 *      the blog trigger(s) [data-blog-search]: true on 'search:open', and false
 *      via a MutationObserver on the drawer's class (covers Escape, the close
 *      button, form submit, and theme-editor section deselect). Bound once,
 *      document-level. No frozen aria-expanded="false".
 *
 * Bootstraps on DOMContentLoaded + shopify:section:load (editor reload).
 * Plain IIFE — leaks nothing to global scope (Rule J8).
 *
 * Rules in force: J1 (addEventListener only), J7 (document-level delegation
 * where shared), J8 (no window.* namespace pollution beyond two dedup flags),
 * J12 (multi-instance-safe).
 */
(function () {
  'use strict';

  function isEditor() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function readStore(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeStore(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode / quota */ }
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  // ---- 1. View switch ---------------------------------------------------
  function applyView(grids, buttons, view) {
    grids.forEach(function (g) { g.setAttribute('data-view', view); });
    buttons.forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-view') === view ? 'true' : 'false');
    });
  }

  function initView(section, handle) {
    var grids = toArray(section.querySelectorAll('[data-blog-grid]'));
    if (!grids.length) return;

    var control = section.querySelector('[data-blog-view-switch]');
    var buttons = control ? toArray(control.querySelectorAll('.segmented-control__btn')) : [];

    var key = 'kv-blog-view' + (handle ? ':' + handle : '');
    var current = grids[0].getAttribute('data-view') || 'grid';
    if (!isEditor()) {
      var stored = readStore(key);
      if (stored === 'grid' || stored === 'list') current = stored;
    }
    applyView(grids, buttons, current);

    if (!control) return;
    control.addEventListener('click', function (event) {
      var btn = event.target.closest('.segmented-control__btn');
      if (!btn || !control.contains(btn)) return;
      var view = btn.getAttribute('data-view');
      if (view !== 'grid' && view !== 'list') return;
      applyView(grids, buttons, view);
      if (!isEditor()) writeStore(key, view);
    });
  }

  // ---- 2. Sort ----------------------------------------------------------
  function indexCards(grid, startFrom) {
    var cards = toArray(grid.querySelectorAll(':scope > .article-card'));
    var n = typeof startFrom === 'number' ? startFrom : 0;
    cards.forEach(function (c) {
      if (!c.hasAttribute('data-natural-index')) {
        c.setAttribute('data-natural-index', String(n));
      }
      n += 1;
    });
    return n;
  }

  function maxIndex(grid) {
    var cards = toArray(grid.querySelectorAll(':scope > .article-card'));
    var m = -1;
    cards.forEach(function (c) {
      var v = parseInt(c.getAttribute('data-natural-index') || '0', 10);
      if (v > m) m = v;
    });
    return m;
  }

  function applySort(grids, order) {
    grids.forEach(function (g) {
      var cards = toArray(g.querySelectorAll(':scope > .article-card'));
      cards.sort(function (a, b) {
        var ai = parseInt(a.getAttribute('data-natural-index') || '0', 10);
        var bi = parseInt(b.getAttribute('data-natural-index') || '0', 10);
        return order === 'oldest' ? bi - ai : ai - bi;
      });
      cards.forEach(function (c) { g.appendChild(c); });
    });
  }

  function initSort(section, handle, state) {
    var grids = toArray(section.querySelectorAll('[data-blog-grid]'));
    grids.forEach(function (g) { indexCards(g, 0); });

    var select = section.querySelector('[data-blog-sort]');
    if (!select) return;

    var key = 'kv-blog-sort' + (handle ? ':' + handle : '');
    if (!isEditor()) {
      var stored = readStore(key);
      if (stored === 'newest' || stored === 'oldest') select.value = stored;
    }
    state.sort = select.value || 'newest';
    if (state.sort === 'oldest') applySort(grids, 'oldest');

    select.addEventListener('change', function () {
      state.sort = select.value;
      applySort(grids, state.sort);
      if (!isEditor()) writeStore(key, state.sort);
    });
  }

  // ---- 3. Load-more / infinite -----------------------------------------
  function initLoadMore(section, state) {
    var wrap = section.querySelector('[data-blog-load-more]');
    if (!wrap) return;
    var grid = section.querySelector('[data-blog-grid]');
    if (!grid) return;

    var btn = wrap.querySelector('[data-blog-load-more-btn]');
    var infinite = wrap.getAttribute('data-mode') === 'infinite';
    var loading = false;
    var io = null;

    function done() {
      wrap.hidden = true;
      if (io) io.disconnect();
    }

    function load() {
      var url = wrap.getAttribute('data-next-url');
      if (loading || !url) return;
      loading = true;
      wrap.setAttribute('data-loading', 'true');

      window.fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = doc.querySelector('[data-blog-grid]');
          var n = maxIndex(grid) + 1;
          if (nextGrid) {
            toArray(nextGrid.querySelectorAll(':scope > .article-card')).forEach(function (card) {
              card.setAttribute('data-natural-index', String(n));
              n += 1;
              grid.appendChild(card);
            });
          }
          var nextWrap = doc.querySelector('[data-blog-load-more]');
          var nextUrl = nextWrap ? nextWrap.getAttribute('data-next-url') : '';
          if (nextUrl) {
            wrap.setAttribute('data-next-url', nextUrl);
          } else {
            wrap.removeAttribute('data-next-url');
            done();
          }
          if (state.sort === 'oldest') applySort([grid], 'oldest');
          loading = false;
          wrap.removeAttribute('data-loading');
        })
        .catch(function () {
          loading = false;
          wrap.removeAttribute('data-loading');
        });
    }

    if (btn) {
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        load();
      });
    }

    if (infinite && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { if (entry.isIntersecting) load(); });
      }, { rootMargin: '600px 0px' });
      io.observe(wrap);
    }
  }

  // ---- 4. Search trigger aria-expanded (global, once) -------------------
  function bindSearchExpanded() {
    if (window.__kvBlogSearchExpandBound) return;
    window.__kvBlogSearchExpandBound = true;

    function reflect(open) {
      var triggers = document.querySelectorAll('[data-blog-search]');
      Array.prototype.forEach.call(triggers, function (t) {
        t.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    function watchDrawer() {
      var drawer = document.querySelector('predictive-search');
      if (!drawer) { window.requestAnimationFrame(watchDrawer); return; }
      if (drawer.__kvBlogExpandWatch) return;
      drawer.__kvBlogExpandWatch = true;
      new MutationObserver(function () {
        if (!drawer.classList.contains('is-open')) reflect(false);
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    document.addEventListener('search:open', function () {
      reflect(true);
      watchDrawer();
    });
  }

  // ---- bootstrap --------------------------------------------------------
  function initSection(section) {
    if (!section || section.dataset.mainBlogInit === 'true') return;
    section.dataset.mainBlogInit = 'true';
    var handle = section.getAttribute('data-blog-handle') || '';
    var state = { sort: 'newest' };
    initView(section, handle);
    initSort(section, handle, state);
    initLoadMore(section, state);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('.section-main-blog'), initSection);
  }

  bindSearchExpanded();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
